import { useEffect, useState } from 'react'
import { BellRing } from 'lucide-react'
import { apiGet } from './primitives.js'
import { CH_LABEL, resumeParCategorie, type Ch, type FamilleInfo, type Origine, type TypeInfo } from '../notification-prefs-model.js'
import '../notifications.css'

/**
 * LES NOTIFICATIONS D'UN UTILISATEUR, VUES PAR UN ADMINISTRATEUR — lecture
 * seule (contrat du 08/10/2026 : export et props `{ userId }` fixés ; la fiche
 * utilisateur l'importe).
 *
 * Ce que la personne reçoit, module par module et canal par canal, et
 * POURQUOI : le droit qui lui manque, son propre réglage, celui de sa
 * catégorie, le réglage proposé par Gestion, ou la politique de
 * l'organisation. Un administrateur ne règle pas les notifications d'autrui :
 * il comprend pourquoi quelqu'un n'a rien reçu.
 *
 * Tous les types réglables y figurent, y compris ceux que la personne ne
 * reçoit pas — c'est précisément la question qu'on vient poser ici.
 */

interface Reponse {
  user: { id: string; display_name: string; active: boolean }
  groups: FamilleInfo[]
  has_saved_prefs: boolean
  quiet_hours: { enabled: boolean; start: string; end: string; allow_critical: boolean } | null
  push_available: boolean
}

type Etat =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; data: Reponse }

const CANAUX_ACTIFS: Ch[] = ['IN_APP', 'EMAIL']

const POURQUOI: Record<Origine, string> = {
  ORGANISATION: 'Politique de l’organisation',
  TYPE: 'Réglé par la personne',
  CATEGORIE: 'Suit son choix par catégorie',
  DEFAUT: 'Réglage proposé par Gestion',
}

function pourquoi(c: TypeInfo['channels'][number]): string {
  if (c.lock === 'MANDATORY') return 'Imposé par l’organisation'
  if (c.lock === 'DISABLED') return 'Coupé par l’organisation'
  return POURQUOI[c.origin]
}

function CelluleLue({ t, canal }: { t: TypeInfo; canal: Ch }) {
  const c = t.channels.find((x) => x.channel === canal)
  if (!t.receivable || !c) {
    return (
      <span role="cell" className="nt-cell">
        <span className="nt-cell__name" aria-hidden="true">{CH_LABEL[canal]}</span>
        <span className="nt-state nt-state--off">Non reçu</span>
      </span>
    )
  }
  // Un type qui part sous plusieurs catégories : ce que chacune donne, pas la
  // seule valeur de la catégorie par défaut (une règle d'alerte Critique part
  // par e-mail même quand une règle Attention n'y part pas).
  const parCat = c.by_category ?? null
  if (parCat && parCat.length > 1 && new Set(parCat.map((x) => x.on)).size > 1) {
    return (
      <span role="cell" className="nt-cell">
        <span className="nt-cell__name" aria-hidden="true">{CH_LABEL[canal]}</span>
        <span className="nt-state nt-state--on">Par catégorie</span>
        <span className="nt-cell__note">
          {resumeParCategorie(parCat.map((x) => ({ categorie: x.category, on: x.on, verrou: x.lock })))}
          {c.type_setting === null || c.type_setting === undefined ? '' : ` · réglé par la personne : ${c.type_setting ? 'activé' : 'coupé'}`}
        </span>
      </span>
    )
  }
  return (
    <span role="cell" className="nt-cell">
      <span className="nt-cell__name" aria-hidden="true">{CH_LABEL[canal]}</span>
      <span className={`nt-state ${c.on ? 'nt-state--on' : 'nt-state--off'}`}>{c.on ? 'Reçu' : 'Coupé'}</span>
      <span className="nt-cell__note">{pourquoi(c)}</span>
    </span>
  )
}

export function UserNotificationPrefsPanel(props: { userId: string }) {
  const [etat, setEtat] = useState<Etat>({ kind: 'loading' })

  useEffect(() => {
    let vivant = true
    setEtat({ kind: 'loading' })
    apiGet<Reponse & { error?: string; message?: string }>(`/api/v1/admin/users/${encodeURIComponent(props.userId)}/notification-preferences`)
      .then((j) => {
        if (!vivant) return
        // `apiGet` RÉSOUT sur une erreur HTTP : on vérifie la forme avant d'afficher.
        if (j && Array.isArray(j.groups)) setEtat({ kind: 'ok', data: j })
        else setEtat({ kind: 'error', message: j?.error === 'FORBIDDEN' ? 'Vous n’avez pas accès aux notifications des utilisateurs.' : j?.message ?? 'Les notifications de ce compte n’ont pas pu être lues.' })
      })
      .catch(() => { if (vivant) setEtat({ kind: 'error', message: 'Les notifications de ce compte n’ont pas pu être lues : le serveur ne répond pas.' }) })
    return () => { vivant = false }
  }, [props.userId])

  return (
    <section className="lc-panel gd-panel nt-panel" aria-labelledby="unp-titre">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="unp-titre">
          <BellRing size={16} aria-hidden="true" className="nt-icon" />Notifications reçues
        </h2>
      </header>
      <div className="lc-panel__body nt-body">
        {etat.kind === 'loading' ? <p className="nt-intro">Lecture des notifications de ce compte</p> : null}
        {etat.kind === 'error' ? <p className="nt-intro" role="alert">{etat.message}</p> : null}
        {etat.kind === 'ok' ? (
          <>
            <p className="nt-intro">
              Ce que {etat.data.user.display_name} reçoit, module par module, et pourquoi. Lecture
              seule : chacun règle ses notifications dans son profil.
              {etat.data.user.active ? '' : ' Ce compte n’est pas actif : il ne reçoit rien tant qu’il ne l’est pas.'}
            </p>
            <p className="nt-intro">
              {etat.data.has_saved_prefs
                ? 'La personne a enregistré ses propres réglages.'
                : 'Aucun réglage personnel enregistré : les réglages proposés par Gestion s’appliquent.'}
              {' '}
              {etat.data.quiet_hours?.enabled
                ? `Heures silencieuses de ${etat.data.quiet_hours.start} à ${etat.data.quiet_hours.end}${etat.data.quiet_hours.allow_critical ? ', le critique passe' : ', même le critique attend'}.`
                : 'Aucune heure silencieuse.'}
            </p>
            {etat.data.groups.map((f) => (
              <div key={f.family_id} className="nt-family">
                <h3 className="nt-family__title">{f.family_label}</h3>
                {f.modules.map((m) => (
                  <div key={m.module_id ?? 'famille'} className="nt-module">
                    <h4 className="nt-module__title">{m.module_label}</h4>
                    <div className="nt-grid" role="table" aria-label={`Notifications reçues — ${f.family_label}, ${m.module_label}`}>
                      <div className="nt-row nt-row--head" role="row">
                        <span role="columnheader">Notification</span>
                        {CANAUX_ACTIFS.map((ch) => <span key={ch} role="columnheader">{CH_LABEL[ch]}</span>)}
                        <span role="columnheader" className="nt-head-soon">
                          {CH_LABEL.PUSH}<span className="nt-head-soon__note">à venir avec l’application mobile</span>
                        </span>
                      </div>
                      {m.types.map((t) => (
                        <div key={t.key} className="nt-row" role="row">
                          <span role="cell" className="nt-type">
                            <span className="nt-type__head">
                              <span className="nt-type__label">{t.label}</span>
                              <span className={`lc-pill nt-cat nt-cat--${t.default_category.toLowerCase()}`}>{t.category_label}</span>
                            </span>
                            {t.receivable ? null : <span className="nt-type__reason">{t.reason ?? 'Non reçu'}</span>}
                          </span>
                          {CANAUX_ACTIFS.map((ch) => <CelluleLue key={ch} t={t} canal={ch} />)}
                          <span role="cell" className="nt-cell nt-cell--soon" aria-label="Push : à venir avec l’application mobile">
                            <span className="nt-cell__name" aria-hidden="true">{CH_LABEL.PUSH}</span>
                            <span className="nt-cell__note">À venir</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </>
        ) : null}
      </div>
    </section>
  )
}
